import React from 'react';
import { FileText, Download, File, FileSpreadsheet, FileArchive, FileCode, FileVideo, FileAudio } from 'lucide-react';
import { AttachmentData } from '../../utils/chatApi';

interface FileCardProps {
  attachment: AttachmentData;
  isMine: boolean;
}

/**
 * Renders a file attachment as a styled card in the chat.
 * Used for non-image files like PDFs, documents, etc.
 */
export const FileCard: React.FC<FileCardProps> = ({ attachment, isMine }) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType: string, filename: string): React.ReactNode => {
    const iconClass = `w-6 h-6 ${isMine ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`;
    
    // Check content type first
    if (contentType.includes('pdf')) {
      return <FileText className={iconClass} />;
    }
    if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType.includes('csv')) {
      return <FileSpreadsheet className={iconClass} />;
    }
    if (contentType.includes('zip') || contentType.includes('archive') || contentType.includes('compressed')) {
      return <FileArchive className={iconClass} />;
    }
    if (contentType.includes('video')) {
      return <FileVideo className={iconClass} />;
    }
    if (contentType.includes('audio')) {
      return <FileAudio className={iconClass} />;
    }
    if (contentType.includes('text') || contentType.includes('json') || contentType.includes('javascript')) {
      return <FileCode className={iconClass} />;
    }
    
    // Check file extension as fallback
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <FileText className={iconClass} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet className={iconClass} />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FileArchive className={iconClass} />;
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'webm':
        return <FileVideo className={iconClass} />;
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
        return <FileAudio className={iconClass} />;
      case 'js':
      case 'ts':
      case 'tsx':
      case 'jsx':
      case 'py':
      case 'html':
      case 'css':
      case 'json':
        return <FileCode className={iconClass} />;
      default:
        return <File className={iconClass} />;
    }
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(attachment.url, '_blank');
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-3 py-2.5 rounded-xl max-w-[280px]
        ${isMine 
          ? 'bg-blue-700 text-white' 
          : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary'
        }
      `}
    >
      {/* File Icon */}
      <div className={`
        flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
        ${isMine ? 'bg-blue-600' : 'bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border'}
      `}>
        {getFileIcon(attachment.content_type, attachment.filename)}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isMine ? 'text-white' : 'text-gray-900 dark:text-dark-text-primary'}`}>
          {attachment.filename}
        </p>
        <p className={`text-xs ${isMine ? 'text-blue-200' : 'text-gray-500 dark:text-dark-text-muted'}`}>
          {formatFileSize(attachment.size_bytes)}
        </p>
      </div>

      {/* Download Button */}
      <button
        onClick={handleDownload}
        className={`
          flex-shrink-0 p-2 rounded-full transition-colors
          ${isMine 
            ? 'hover:bg-blue-600 text-white' 
            : 'hover:bg-gray-200 dark:hover:bg-dark-bg-hover text-gray-600 dark:text-dark-text-secondary'
          }
        `}
        title="Download"
      >
        <Download className="w-4 h-4" />
      </button>
    </div>
  );
};

export default FileCard;

